// F7: the local cache. The dashboard, list and search read only from here, so they work offline.
import Dexie, { type EntityTable } from "dexie";
import type { Contract, ContractFields, Outcome } from "../../shared/contract";

export type OutboxOp =
  | { kind: "create"; contractId: string; fields: ContractFields }
  | { kind: "update"; contractId: string; baseVersion: number; fields: Partial<ContractFields> }
  | { kind: "delete"; contractId: string }
  | { kind: "outcome"; contractId: string; baseVersion: number; outcome: Outcome; newEndDate: string | null };

export type OutboxEntry = OutboxOp & { id?: number; createdAt: string; attempts: number; lastError?: string };

export interface ConflictNote {
  id?: number;
  contractId: string;
  title: string;
  message: string;
  createdAt: string;
}

export interface LocalContract extends Contract {
  /** True while a local change hasn't reached the server yet. */
  pending?: boolean;
}

export class ClauseKeepDB extends Dexie {
  contracts!: EntityTable<LocalContract, "id">;
  outbox!: EntityTable<OutboxEntry, "id">;
  conflicts!: EntityTable<ConflictNote, "id">;
  meta!: EntityTable<{ key: string; value: unknown }, "key">;

  constructor(name = "clausekeep") {
    super(name);
    this.version(1).stores({
      contracts: "id, seq, deletedAt",
      outbox: "++id, contractId",
      conflicts: "++id, contractId",
      meta: "key",
    });
    // Contracts cached before types, people and terms existed get the empty defaults.
    this.version(2).stores({}).upgrade((tx) =>
      tx
        .table("contracts")
        .toCollection()
        .modify((c: Partial<LocalContract>) => {
          c.type ??= null;
          c.parties ??= [];
          c.details ??= {};
        }),
    );
  }
}

export const db = new ClauseKeepDB();

export async function getMeta<T>(database: ClauseKeepDB, key: string, fallback: T): Promise<T> {
  const row = await database.meta.get(key);
  return row ? (row.value as T) : fallback;
}

export async function setMeta(database: ClauseKeepDB, key: string, value: unknown): Promise<void> {
  await database.meta.put({ key, value });
}

/** Everything on this device is removed on sign-out, so the next person on a shared computer sees nothing. */
export async function wipeLocalData(database: ClauseKeepDB = db): Promise<void> {
  await Promise.all([database.contracts.clear(), database.outbox.clear(), database.conflicts.clear(), database.meta.clear()]);
  if (typeof caches !== "undefined") {
    const keys = await caches.keys();
    await Promise.all(keys.filter((k) => k.startsWith("ck-files")).map((k) => caches.delete(k)));
  }
}
